<template>
    <Head title="Search Results" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="mb-8">
            <h1 class="text-3xl font-bold text-secondary-900 dark:text-white">Search Results</h1>
            <p class="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
                Found <span class="font-semibold text-primary-600 dark:text-primary-400">{{ totalCount }}</span> results for
                "<span class="font-semibold">{{ query }}</span>"
            </p>
        </div>

        <!-- Search Bar -->
        <div class="mb-6">
            <div class="relative">
                <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg class="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <input
                    type="search"
                    v-model="localQuery"
                    @keyup.enter="performSearch"
                    class="block w-full p-3 pl-10 text-sm text-secondary-900 border border-secondary-300 rounded-lg bg-white focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:border-secondary-600 dark:placeholder-secondary-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                    placeholder="Search tickets, work orders, incidents, assets, equipment, users..."
                    autofocus
                />
                <button
                    v-if="localQuery"
                    @click="clearSearch"
                    type="button"
                    class="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200"
                >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Filters -->
        <div class="mb-6 flex flex-wrap gap-2">
            <button
                v-for="moduleFilter in moduleFilters"
                :key="moduleFilter.value"
                @click="toggleModuleFilter(moduleFilter.value)"
                :class="[
                    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    activeModuleFilter === moduleFilter.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                ]"
            >
                <div class="flex items-center space-x-2">
                    <component :is="moduleFilter.icon" class="w-4 h-4" />
                    <span>{{ moduleFilter.label }}</span>
                    <span v-if="getModuleCount(moduleFilter.value) > 0" class="px-2 py-0.5 rounded-full text-xs" :class="activeModuleFilter === moduleFilter.value ? 'bg-primary-700' : 'bg-secondary-200 dark:bg-secondary-700'">
                        {{ getModuleCount(moduleFilter.value) }}
                    </span>
                </div>
            </button>
        </div>

        <!-- Results -->
        <div v-if="filteredResults.length > 0" class="space-y-6">
            <!-- Group by Module -->
            <div v-for="(group, moduleName) in groupedResults" :key="moduleName" class="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                <!-- Module Header -->
                <div class="px-6 py-4 bg-secondary-50 dark:bg-secondary-750 border-b border-secondary-200 dark:border-secondary-700">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-3">
                            <div class="p-2 rounded-lg" :class="getModuleColor(moduleName)">
                                <component :is="getModuleIcon(moduleName)" class="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 class="text-lg font-semibold text-secondary-900 dark:text-white capitalize">{{ getModuleLabel(moduleName) }}</h2>
                                <p class="text-sm text-secondary-600 dark:text-secondary-400">{{ group.length }} {{ group.length === 1 ? 'result' : 'results' }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Results List -->
                <div class="divide-y divide-secondary-200 dark:divide-secondary-700">
                    <Link
                        v-for="result in group"
                        :key="result.id"
                        :href="result.url"
                        class="block px-6 py-4 hover:bg-secondary-50 dark:hover:bg-secondary-750 transition-colors"
                    >
                        <div class="flex items-start justify-between">
                            <div class="flex-1 min-w-0">
                                <div class="flex items-center space-x-2 mb-1">
                                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium" :class="getStatusColor(result.status)">
                                        {{ result.status }}
                                    </span>
                                    <span class="text-sm font-mono text-secondary-500 dark:text-secondary-400">{{ result.subtitle }}</span>
                                </div>
                                <h3 class="text-base font-semibold text-secondary-900 dark:text-white mb-1" v-html="highlightQuery(result.title)"></h3>
                                <p class="text-sm text-secondary-600 dark:text-secondary-400 line-clamp-2" v-html="highlightQuery(result.description)"></p>
                            </div>
                            <div class="ml-4 flex-shrink-0 text-right">
                                <p class="text-xs text-secondary-500 dark:text-secondary-400">{{ formatDate(result.created_at) }}</p>
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
        </div>

        <!-- No Results -->
        <div v-else class="text-center py-12 bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700">
            <svg class="mx-auto h-12 w-12 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 class="mt-4 text-lg font-medium text-secondary-900 dark:text-white">No results found</h3>
            <p class="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
                Try adjusting your search or filters to find what you're looking for.
            </p>
        </div>

        <!-- Quick Tips -->
        <div class="mt-8 bg-primary-50 dark:bg-primary-900/20 rounded-lg p-6 border border-primary-200 dark:border-primary-800">
            <h3 class="text-sm font-semibold text-primary-900 dark:text-primary-300 mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Search Tips
            </h3>
            <ul class="space-y-2 text-sm text-primary-800 dark:text-primary-300">
                <li class="flex items-start">
                    <span class="mr-2">•</span>
                    <span>Search by ID numbers (e.g., TKT-001, WO-123, AST-456)</span>
                </li>
                <li class="flex items-start">
                    <span class="mr-2">•</span>
                    <span>Search by titles, descriptions, or names</span>
                </li>
                <li class="flex items-start">
                    <span class="mr-2">•</span>
                    <span>Use filters to narrow down results by type</span>
                </li>
                <li class="flex items-start">
                    <span class="mr-2">•</span>
                    <span>Press Ctrl+K or Cmd+K to quickly access search from anywhere</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';

const props = defineProps({
    query: {
        type: String,
        required: true,
    },
    results: {
        type: [Array, Object],
        required: true,
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
    totalCount: {
        type: Number,
        default: 0,
    },
});

// Get subscription features and auth from Inertia page props
import { usePage } from '@inertiajs/vue3';
const page = usePage();
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });
const userPermissions = computed(() => page.props.auth?.permissions || []);

// Local state
const localQuery = ref(props.query);
const activeModuleFilter = ref(props.filters.module || 'all');

// Module filters configuration - filtered based on subscription and permissions
const moduleFilters = computed(() => {
    const allFilters = [
        { value: 'all', label: 'All Results', icon: 'IconAll' },
        { value: 'tickets', label: 'Tickets', icon: 'IconTicket' },
        { value: 'work_orders', label: 'Work Orders', icon: 'IconWorkOrder' },
        { value: 'incidents', label: 'Incidents', icon: 'IconIncident' },
        { value: 'assets', label: 'Assets', icon: 'IconAsset' },
        { value: 'equipment', label: 'IT Equipment', icon: 'IconEquipment' },
        { value: 'users', label: 'Users', icon: 'IconUser' },
        { value: 'locations', label: 'Locations', icon: 'IconLocation' },
        { value: 'services', label: 'Services', icon: 'IconService' },
    ];

    // Filter out modules based on subscription features and permissions
    return allFilters.filter(filter => {
        // 'All Results' is always shown
        if (filter.value === 'all') return true;

        // Assets require asset_module subscription feature
        if (filter.value === 'assets') {
            return subscriptionFeatures.value.asset_module === true || subscriptionFeatures.value.asset_module === 'true';
        }

        // IT Equipment requires equipment_module subscription feature
        if (filter.value === 'equipment') {
            return subscriptionFeatures.value.equipment_module === true || subscriptionFeatures.value.equipment_module === 'true';
        }

        // All other filters (including Locations) are shown by default
        // The backend will handle permission filtering
        return true;
    });
});

// Convert results to array if needed
const resultsArray = computed(() => {
    if (Array.isArray(props.results)) {
        return props.results;
    }
    return Object.values(props.results);
});

// Filtered results
const filteredResults = computed(() => {
    if (activeModuleFilter.value === 'all') {
        return resultsArray.value;
    }
    return resultsArray.value.filter(result => result.module === activeModuleFilter.value);
});

// Group results by module
const groupedResults = computed(() => {
    const grouped = {};
    filteredResults.value.forEach(result => {
        if (!grouped[result.module]) {
            grouped[result.module] = [];
        }
        grouped[result.module].push(result);
    });
    return grouped;
});

// Get count for each module
const getModuleCount = (module) => {
    if (module === 'all') return resultsArray.value.length;
    return resultsArray.value.filter(r => r.module === module).length;
};

// Toggle module filter
const toggleModuleFilter = (module) => {
    activeModuleFilter.value = module;
};

// Perform new search
const performSearch = () => {
    if (localQuery.value.trim()) {
        router.get(route('search'), { q: localQuery.value });
    }
};

// Clear search
const clearSearch = () => {
    localQuery.value = '';
    router.visit(route('dashboard'));
};

// Highlight query in text
const highlightQuery = (text) => {
    if (!text || !props.query) return text;
    const regex = new RegExp(`(${props.query})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800/50 dark:text-yellow-200 px-1 rounded">$1</mark>');
};

// Format date
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - d);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return d.toLocaleDateString();
};

// Get module label
const getModuleLabel = (module) => {
    const labels = {
        tickets: 'Tickets',
        work_orders: 'Work Orders',
        incidents: 'Incidents',
        assets: 'Assets',
        equipment: 'IT Equipment',
        users: 'Team Members',
        locations: 'Locations',
        services: 'Services',
    };
    return labels[module] || module;
};

// Get module icon
const getModuleIcon = (module) => {
    return 'IconModule';
};

// Get module color
const getModuleColor = (module) => {
    const colors = {
        tickets: 'bg-blue-600',
        work_orders: 'bg-purple-600',
        incidents: 'bg-red-600',
        assets: 'bg-green-600',
        equipment: 'bg-teal-600',
        users: 'bg-indigo-600',
        locations: 'bg-yellow-600',
        services: 'bg-pink-600',
    };
    return colors[module] || 'bg-secondary-600';
};

// Get status color
const getStatusColor = (status) => {
    if (!status) return 'bg-secondary-100 text-secondary-800 dark:bg-secondary-700 dark:text-secondary-300';

    const statusLower = status.toLowerCase();

    if (statusLower.includes('open') || statusLower.includes('active') || statusLower.includes('operational')) {
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    }
    if (statusLower.includes('progress') || statusLower.includes('assigned')) {
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    }
    if (statusLower.includes('pending') || statusLower.includes('hold')) {
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    }
    if (statusLower.includes('closed') || statusLower.includes('completed') || statusLower.includes('resolved')) {
        return 'bg-secondary-100 text-secondary-800 dark:bg-secondary-700 dark:text-secondary-300';
    }
    if (statusLower.includes('critical') || statusLower.includes('urgent') || statusLower.includes('down')) {
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    }

    return 'bg-secondary-100 text-secondary-800 dark:bg-secondary-700 dark:text-secondary-300';
};

// Icon components (simple SVG icons)
const IconAll = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" /></svg>`
};

const IconTicket = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>`
};

const IconWorkOrder = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>`
};

const IconIncident = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>`
};

const IconAsset = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>`
};

const IconUser = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>`
};

const IconLocation = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>`
};

const IconService = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>`
};

const IconEquipment = {
    template: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>`
};

const IconModule = {
    template: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>`
};
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
